import {
  Button,
  Col,
  Input,
  Layout,
  Row,
  Space,
  Table,
  TablePaginationConfig,
  Typography,
} from "antd";
import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, UseMutationResult, useQuery } from "react-query";

import { appInject } from "@core/di/utils";
import { DI_TOKENS } from "@shared/constants/di";
import { ITiersService } from "@shared/interfaces/tiers-service.interface";
import {
  CreateTierDto,
  TierDto,
  UpdateTierDto,
} from "@shared/types/tiers/tiers.dto";
import { ColumnsType } from "antd/es/table";

import dayjs from "dayjs";
import ChangeTierModal from "./ChangeTierModal/ChangeTierModal";
import ManageBaselinesModal from "./ManageBaselinesModal/ManageBaselinesModal";

const Tiers = () => {
  const [searchText, setSearchText] = useState("");
  const [debouncedSearchText, setDebouncedSearchText] = useState(searchText);

  const [page, setPage] = useState(1);
  const [currentPageSize, setPageSize] = useState(20);

  const [containerHeight, setContainerHeight] = useState(0);
  const [isOpenModal, setIsOpenModal] = useState<boolean>(false);
  const [isBaselinesModalOpen, setIsBaselinesModalOpen] = useState(false);
  const [selectedTier, setSelectedTier] = useState<TierDto | null>(null);

  const tiersService = appInject<ITiersService>(DI_TOKENS.tiersService);

  const columns = useMemo(
    () =>
      [
        {
          key: "action",
          title: "Actions",
          render: (_: null, tier: TierDto) => {
            return (
              <Space size="middle">
                <Button
                  size="small"
                  onClick={() => {
                    setSelectedTier(tier);
                    setIsBaselinesModalOpen(true);
                  }}
                >
                  View
                </Button>
                <Button
                  type="primary"
                  size="small"
                  onClick={() => {
                    setSelectedTier(tier);
                    setIsOpenModal(true);
                  }}
                >
                  Edit
                </Button>
              </Space>
            );
          },
          width: 180,
        },
        {
          key: "icon",
          dataIndex: "icon",
          title: "Icon",
          width: 80,
          render: (icon: string) =>
            icon ? (
              <img
                src={icon}
                alt="icon"
                style={{ height: 40, width: 40, objectFit: "contain" }}
              />
            ) : (
              "-"
            ),
        },
        {
          key: "name",
          dataIndex: "name",
          title: "Name",
          width: 150,
        },
        {
          key: "rank",
          dataIndex: "rank",
          title: "Tier Entry Threshold (GLO)",
          width: 150,
          sorter: (a, b) => a.rank - b.rank,
        },
        {
          key: "gloThreshold",
          dataIndex: "gloThreshold",
          title: "Tier Base GLO value",
          width: 150,
        },
        {
          key: "percentile",
          dataIndex: "percentile",
          title: "Percentile",
          width: 100,
        },
        {
          key: "showGlobalRank",
          dataIndex: "showGlobalRank",
          title: "Show global rank",
          width: 120,
          render: (value: boolean) => (value ? "Yes" : "No"),
        },
        {
          key: "createdAt",
          dataIndex: "createdAt",
          title: "CreatedAt",
          width: 200,
          render: (date: Date | null) =>
            date ? dayjs(date).format("YYYY-MM-DD hh:mm:ss A") : "N/A",
        },
      ] as ColumnsType<TierDto>,

    [],
  );

  const tiersQuery = useQuery(
    ["download-tiers-list", page, currentPageSize, debouncedSearchText],
    () =>
      tiersService.list({
        page,
        pageSize: currentPageSize,
        query: debouncedSearchText,
      }),

    {
      refetchOnMount: true,
      keepPreviousData: true,
    },
  );

  const updateTierMutation: UseMutationResult<
    TierDto,
    unknown,
    UpdateTierDto,
    unknown
  > = useMutation(
    (data: UpdateTierDto): Promise<TierDto> => tiersService.update(data),
    {
      onSuccess: () => {
        setSelectedTier(null);
        setIsOpenModal(false);
        tiersQuery.refetch();
      },
    },
  );

  const createTierMutation: UseMutationResult<
    TierDto,
    unknown,
    CreateTierDto,
    unknown
  > = useMutation(
    (data: CreateTierDto): Promise<TierDto> => tiersService.create(data),
    {
      onSuccess: () => {
        setSelectedTier(null);
        setIsOpenModal(false);
        tiersQuery.refetch();
      },
    },
  );

  const handleCloseDialog = () => {
    setIsOpenModal(false);
    setSelectedTier(null);
    createTierMutation.reset();
    updateTierMutation.reset();
  };

  const handleSaveDialog = (data: CreateTierDto) => {
    if (selectedTier?.id) {
      updateTierMutation.mutate({ ...data, id: selectedTier.id });
    } else {
      createTierMutation.mutate(data);
    }
  };

  const containerRef = useRef<HTMLDivElement>(null);

  const onTableChange = ({ current, pageSize }: TablePaginationConfig) => {
    setPage(current || 1);
    if (pageSize !== currentPageSize) {
      setPageSize(pageSize || 20);
      setPage(1);
    }
  };

  useEffect(() => {
    setTimeout(() => {
      if (containerRef.current?.offsetHeight) {
        setContainerHeight(containerRef.current.offsetHeight - 60);
      }
    }, 100);
  }, []);

  useEffect(() => {
    const handler = setTimeout(() => {
      setPage(1);
      setDebouncedSearchText(searchText);
    }, 300);
    return () => clearTimeout(handler);
  }, [searchText]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  };

  return (
    <Layout style={{ height: "100%", padding: "20px" }}>
      {isOpenModal && (
        <ChangeTierModal
          open={isOpenModal}
          tier={selectedTier}
          onClose={handleCloseDialog}
          onSave={handleSaveDialog}
          isLoading={
            updateTierMutation.isLoading || createTierMutation.isLoading
          }
          isError={updateTierMutation.isError || createTierMutation.isError}
        />
      )}

      {isBaselinesModalOpen && (
        <ManageBaselinesModal
          open={isBaselinesModalOpen}
          tier={selectedTier}
          onClose={() => {
            setIsBaselinesModalOpen(false);
            setSelectedTier(null);
          }}
        />
      )}

      <Row
        style={{
          height: 40,
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 20,
        }}
      >
        <Col>
          <Space size="large">
            <Typography.Title level={4} style={{ margin: 0 }}>
              Tiers ({tiersQuery.data?.meta?.total || 0})
            </Typography.Title>
            <Button
              type="primary"
              onClick={() => {
                setSelectedTier(null);
                setIsOpenModal(true);
              }}
            >
              Add Tier
            </Button>
          </Space>
        </Col>
        <Col>
          <Input
            placeholder="Search by Name"
            value={searchText}
            onChange={handleSearch}
            style={{ minWidth: 250 }}
          />
        </Col>
      </Row>

      <Layout.Content
        ref={containerRef as any}
        style={{
          backgroundColor: "white",
          flex: 1,
          overflow: "hidden",
          borderRadius: 8,
        }}
      >
        <Table
          rowKey="id"
          dataSource={tiersQuery.data?.data || []}
          onChange={onTableChange}
          columns={columns}
          loading={tiersQuery.isFetching}
          pagination={{
            current: page,
            total: tiersQuery.data?.meta?.total || 0,
            pageSize: currentPageSize,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} items`,
          }}
          size="small"
          scroll={{ y: containerHeight, x: "max-content" }}
        />
      </Layout.Content>
    </Layout>
  );
};

export default Tiers;
